import React from 'react'
import { Card, Button, StyledText, UsageBar, Tooltip } from '@clickcease/cc-client-components-lib'
import {
  cardStyle,
  CardTitle,
  CardDivider,
  CardText,
  CardUsageText,
  UsageBarWrapper,
  CardAdditionalRateText,
  CardPaceTextWrapper,
  CardMonthsView,
  CardMonthsBoxWrapper,
  CardMonthBoxTitle,
  CardMonthBoxValue,
  CardMonthBox,
  CardUsageSpan,
  ButtonWrapperForTooltip,
} from './DataUsageProductCard.styled'
import { PackageUsage, USAGE_KEYS, UsageKeys } from 'Features/accountSettings/api/getAccountUsage'
import { Trans, useTranslation } from 'react-i18next'
import { StringMap } from 'i18next'
import { differenceInMonths, format, isAfter } from 'date-fns'
import { commaSeparatedNumber, limitDecimal } from 'Utils/number'
import { useNavigate } from 'react-router-dom'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { CURRENCIES, CurrenciesTypes } from 'Types/Currencies'
import { getOverage } from './overageHelper'
import { useZendeskChat } from 'Hooks/zendesk/useZendeskChat'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { use12InstallmentsAllowChanges } from 'Hooks/split/use12InstallmentsChanges'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'

const CURRENT_MONTH_NUMBER = new Date().getMonth()

const MONTHS = {
  [USAGE_KEYS.currentMonth]: CURRENT_MONTH_NUMBER,
  [USAGE_KEYS.lastMonth]: CURRENT_MONTH_NUMBER - 1,
  [USAGE_KEYS.lastTwoMonths]: CURRENT_MONTH_NUMBER - 2,
}

export const DataUsageProductCard = ({ item }: { item: PackageUsage }) => {
  const { openChat } = useZendeskChat()
  const { t } = useTranslation('accountSettings')
  const navigate = useNavigate()
  const { productsInTrial } = useIsProductTrial()
  const { data: plansDetails } = useGetPlansDetails()
  const { product, visitsCount, limit, usePercentage, usage, pace } = item
  const { isInstallmentsSubscription } = useUserStore()
  const split12InstallmentsAllowChanges = use12InstallmentsAllowChanges()
  const isTrialSubscription = useIsTrialSubscription()

  const showUpgrade =
    !productsInTrial &&
    (!isInstallmentsSubscription ||
      (isInstallmentsSubscription && (isTrialSubscription || split12InstallmentsAllowChanges)))

  const showDisableUpgrade = !showUpgrade && !!productsInTrial

  const showPace =
    !!pace && differenceInMonths(new Date(), new Date(plansDetails?.startDate || new Date())) > 1

  const showRate = plansDetails?.currentPeriodStart
    ? isAfter(new Date(plansDetails.currentPeriodStart), new Date('2023-07-08'))
    : false

  const translate = (key: string, options?: StringMap) => t(`data_usage.card.${key}`, options)

  const currency: CurrenciesTypes =
    (plansDetails?.plans.length && plansDetails?.plans[0].currency) || CURRENCIES.USD
  const rate = getOverage(product, currency)

  const onUpgradePlan = () => navigate('/modify-plan')

  const getMonthName = (key: UsageKeys) =>
    `${format(new Date(2023, MONTHS[key]), 'MMMM')} ${
      key === USAGE_KEYS.currentMonth ? translate('current') : ''
    }`

  return (
    <Card css={cardStyle}>
      <CardTitle>{translate(`${product}_title`)}</CardTitle>
      <StyledText variant="baseBold">{translate('total_annual_visits_used')} </StyledText>
      <CardUsageText>
        {translate('usage', {
          used: commaSeparatedNumber(visitsCount),
          available: commaSeparatedNumber(limit),
          percentage: limitDecimal(usePercentage),
        })}
      </CardUsageText>

      <UsageBarWrapper>
        <UsageBar percent={usePercentage} highlightDisallowed />
      </UsageBarWrapper>

      <CardPaceTextWrapper>
        {usePercentage >= 100 && (
          <Trans
            ns="accountSettings"
            i18nKey={showRate ? 'card.exceeded_error' : 'exceeded_error_no_rate'}
          >
            <CardText error>
              {`You've exceeded your plan's limit.` +
                (showRate
                  ? ` Upgrade your plan to avoid additional rates.`
                  : ` Please upgrade your plan.`)}
              <CardUsageSpan onClick={openChat}>Contact us</CardUsageSpan>
            </CardText>
          </Trans>
        )}
        {showPace && usePercentage <= 100 && (
          <CardText error={true}>
            {translate('based_current_pace', {
              percentage: limitDecimal(pace, 2),
            })}
          </CardText>
        )}
      </CardPaceTextWrapper>

      {showRate && rate && (
        <CardAdditionalRateText>
          {translate('additional_visits', { rate })}
          <Tooltip tooltiptContent={translate('additional_visits_tooltip')} />
        </CardAdditionalRateText>
      )}

      {showUpgrade && <Button text={translate('card_button')} onClick={onUpgradePlan} />}
      {showDisableUpgrade && (
        <Tooltip tooltiptContent={translate('card_button_tooltip')}>
          <ButtonWrapperForTooltip>
            <Button text={translate('card_button')} disabled />
          </ButtonWrapperForTooltip>
        </Tooltip>
      )}

      <CardDivider />

      <CardMonthsView>
        <StyledText variant="largeBold">{translate('3_moths')}</StyledText>
        <CardMonthsBoxWrapper>
          {Object.entries(usage).map(([month, usageItem]) => (
            <CardMonthBox key={month}>
              <CardMonthBoxTitle>{getMonthName(month as UsageKeys)}</CardMonthBoxTitle>
              <CardMonthBoxValue>{commaSeparatedNumber(usageItem || 0)}</CardMonthBoxValue>
            </CardMonthBox>
          ))}
        </CardMonthsBoxWrapper>
      </CardMonthsView>
    </Card>
  )
}
